import React, { useState, useEffect, useCallback } from "react";
import { Grid, TextField, Autocomplete } from "@mui/material";
import { Button, DatePicker, Typography } from "antd";
import dayjs from "dayjs";
import { getUser } from "../../services/user.service";
import { getAllProject, getTaskReport, downloadTaskReport } from "../../services/reports.service";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { Box } from "@mui/system";
import { ISSUETYPE } from "../../common/constant/constant";

const { RangePicker } = DatePicker;

const TaskReport = () => {
    const initialMonth = dayjs();
    const [dateRange, setDateRange] = useState({
        startDate: initialMonth.startOf("month"),
        endDate: initialMonth,
    });
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState({ _id: "All", name: "All" });
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState({ _id: "All", name: "All" });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [grandTotal, setGrandTotal] = useState("0.00");
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [selectedIssueType, setSelectedIssueType] = useState({ value: "All", label: "All" });
    const [payableFilter, setPayableFilter] = useState("All");

    const payableOptions = [
        { label: "All", value: "All" },
        { label: "Payable", value: "true" },
        { label: "Unpayable", value: "false" },
    ];

    // Create issue types array from constant with numeric values
    const issueTypes = [
        { value: "All", label: "All" },
        { value: ISSUETYPE.BUG, label: "Bug" },
        { value: ISSUETYPE.TASK, label: "Task" },
        { value: ISSUETYPE.TESTING, label: "Testing" },
        { value: ISSUETYPE.DESIGNING, label: "Designing" },
        { value: ISSUETYPE.OTHERS, label: "Others" },
        { value: ISSUETYPE.MEETING, label: "Meeting" },
        { value: ISSUETYPE.DEPLOYMENT, label: "Deployment" },
    ];

    // Fetch users
    useEffect(() => {
        getUser({ isEnabled: true }).then((res) => {
            const userList = Array.isArray(res.data?.data) ? res.data.data : [];
            const formattedUsers = userList.map((u) => ({
                ...u,
                name: `${u.firstName}${u.lastName ? " " + u.lastName : ""}`,
            }));
            setUsers([{ _id: "All", name: "All" }, ...formattedUsers]);
        });
    }, []);

    // Fetch projects
    useEffect(() => {
        getAllProject({ isEnabled: true }).then((res) => {
            const projectList = Array.isArray(res.data) ? res.data : [];
            setProjects([{ _id: "All", name: "All" }, ...projectList]);
        });
    }, []);

    // Fetch report data
    const fetchReport = async (params = {}) => {
        setLoading(true);
        try {
            // MasterCommonTable sends 1-based page, convert to 0-based
            const pageIndex = params.page ? params.page - 1 : 0;
            // Use passed limit, or current state, or default 10
            const limit = params.limit || rowsPerPage || 10;
            const sortBy = params.sort ? params.sort.replace("-", "") : "projectName";
            const sortOrder = params.sortType || "asc";

            const res = await getTaskReport({
                startDate: dateRange.startDate
                    ? dateRange.startDate.startOf("day").toISOString()
                    : null,
                endDate: dateRange.endDate
                    ? dateRange.endDate.endOf("day").toISOString()
                    : null,
                userId: selectedUser?._id || "All",
                projectId: selectedProject?._id || "All",
                issueType: selectedIssueType?.value !== undefined ? selectedIssueType.value : "All",
                page: pageIndex,
                limit,
                sortBy,
                sortOrder,
                payable: payableFilter,
            });

            const reportData = res.data?.data?.data || res.data?.data || [];
            const totalCount = res.data?.data?.total || res.data?.total || 0;

            setData(reportData);
            setTotal(totalCount);
            setGrandTotal(res.data?.grandTotalHours || 0); // Corrected from grandTotal to grandTotalHours
        } catch (error) {
            console.error("Error fetching task report:", error);
            setData([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    // Initial load and filter changes
    useEffect(() => {
        // Fetch page 0 when filters change
        // We reset limit to 10 here because the table remounts (key changes) and resets its internal state to 10
        setRowsPerPage(10);
        setPage(0);
        setPage(0);
        fetchReport({ page: 1, limit: 10 });
    }, [dateRange, selectedUser, selectedProject, selectedIssueType, payableFilter]);

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const response = await downloadTaskReport({
                startDate: dateRange.startDate
                    ? dateRange.startDate.startOf("day").toISOString()
                    : null,
                endDate: dateRange.endDate
                    ? dateRange.endDate.endOf("day").toISOString()
                    : null,
                userId: selectedUser?._id || "All",
                projectId: selectedProject?._id || "All",
                issueType: selectedIssueType?.value !== undefined ? selectedIssueType.value : "All",
            });

            // For blob responses, the interceptor returns the data (blob) directly
            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            // Check if blob has content
            if (blob.size === 0) {
                console.error("Received empty file from server");
                return;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Generate filename with date range
            const startStr = dateRange.startDate.format("YYYY-MM-DD");
            const endStr = dateRange.endDate.format("YYYY-MM-DD");
            link.setAttribute("download", `Task_Report_${startStr}_to_${endStr}.xlsx`);

            document.body.appendChild(link);
            link.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);

        } catch (error) {
            console.error("Error downloading report:", error);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleTableChange = (params) => {
        // params: { page, limit, sortType, sort }
        console.log("handleTableChange params:", params);
        if (params.limit) setRowsPerPage(params.limit);
        if (params.page) setPage(params.page - 1); // Track internal 0-based page
        fetchReport(params);
    };

    const columns = [
        {
            id: "projectName",
            label: "Project Name",
            minWidth: 200,
        },
        {
            id: "taskNo",
            label: "Task No",
            minWidth: 100,
        },
        {
            id: "title",
            label: "Title",
            minWidth: 250,
        },
        {
            id: "description",
            label: "Description",
            minWidth: 300,
        },
        {
            id: "totalHours",
            label: "Working Time",
            minWidth: 120,
            isTotal: true,
            totalType: "time",
        },
    ];

    // Transform data with proper time formatting
    const transformedData = data.map((item) => {
        const hours = Math.floor(item.totalHours || 0);
        const mins = Math.round(((item.totalHours || 0) % 1) * 100);

        // Strip HTML tags from description
        const stripHtml = (html) => {
            if (!html) return "N/A";
            const tmp = document.createElement("DIV");
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || "N/A";
        };

        return {
            projectName: item.projectName || "N/A",
            taskNo: item.taskNo || "N/A",
            title: item.title || "N/A",
            description: stripHtml(item.description),
            totalHours: `${hours}.${mins.toString().padStart(2, "0")}`,
        };
    });

    const tableConfig = {
        isAction: false,
        isEdit: false,
        isMore: false,
        isAdmin: false,
    };

    return (
        <div className="app-container">
            <Grid container spacing={2} mb={2}>
                <Grid item>
                    <RangePicker
                        value={[
                            dateRange.startDate ? dayjs(dateRange.startDate) : null,
                            dateRange.endDate ? dayjs(dateRange.endDate) : null,
                        ]}
                        onChange={(dates) => {
                            const [start, end] = dates || [];
                            if (start && end) {
                                setDateRange({
                                    startDate: dayjs(start).startOf("day"),
                                    endDate: dayjs(end).endOf("day"),
                                });
                            } else {
                                const initialMonth = dayjs();
                                setDateRange({
                                    startDate: initialMonth.startOf("month"),
                                    endDate: initialMonth.endOf("month"),
                                });
                            }
                        }}
                        className="datePicker w-100"
                        format={"DD-MM-YYYY"}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={users}
                        getOptionLabel={(u) => u.name || ""}
                        value={selectedUser}
                        onChange={(e, val) => setSelectedUser(val || { _id: "All", name: "All" })}
                        renderInput={(params) => (
                            <TextField {...params} label="Select User" />
                        )}
                        sx={{ width: 200 }}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={projects}
                        getOptionLabel={(p) => p.name || ""}
                        value={selectedProject}
                        onChange={(e, val) => setSelectedProject(val || { _id: "All", name: "All" })}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Project" />
                        )}
                        sx={{ width: 200 }}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={issueTypes}
                        getOptionLabel={(option) => option.label || ""}
                        value={selectedIssueType}
                        onChange={(e, val) => setSelectedIssueType(val || { value: "All", label: "All" })}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Issue Type" />
                        )}
                        sx={{ width: 130 }}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={payableOptions}
                        getOptionLabel={(option) => option.label}
                        value={payableOptions.find((opt) => opt.value === payableFilter) || payableOptions[0]}
                        onChange={(e, val) => setPayableFilter(val ? val.value : "All")}
                        renderInput={(params) => <TextField {...params} label="Payable" />}
                        sx={{ width: 100 }}
                        disableClearable
                    />
                </Grid>
                <Grid item display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                    <Button
                        color="success"
                        onClick={handleDownload}
                        disabled={downloadLoading}
                        style={{
                            backgroundColor: "#008080",
                            color: "white",
                            padding: "10px 24px",
                            height: "37px",
                            fontSize: "14px",
                            fontWeight: 500,
                        }}
                    >
                        {downloadLoading ? "Downloading..." : "Excel Data"}
                    </Button>

                    {grandTotal && (
                        <Box
                            sx={{
                                bgcolor: "#008080",
                                color: "white",
                                padding: "8px 24px",
                                borderRadius: 2,
                                boxShadow: "0 4px 12px rgba(0, 128, 128, 0.25)",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography style={{ fontWeight: 500, color: "#ffffff" }}>
                                Total Hours:
                            </Typography>
                            <Typography style={{ fontWeight: 500, color: "#ffffffff" }}>
                                {grandTotal}
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            <MasterCommonTable
                // Use key to force verify re-mount when filters change, resetting internal page state
                key={`${selectedUser?._id}-${selectedProject?._id}-${dateRange.startDate?.toISOString()}-${dateRange.endDate?.toISOString()}`}
                columns={columns}
                data={transformedData}
                tableConfig={tableConfig}
                isServerSide={true}
                tableTotalCount={total}
                tableOutputEvent={handleTableChange}
                openLoader={loading}
            />
        </div>
    );
};

export default TaskReport;
